<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import DriverAppLayout from '@/components/layout/DriverAppLayout.vue'
import DriverCard from '@/components/dashboard/DriverCard.vue'
import DriverMetric from '@/components/dashboard/DriverMetric.vue'
import DriverRow from '@/components/dashboard/DriverRow.vue'
import DriverAction from '@/components/dashboard/DriverAction.vue'

type Payload = {
  driver?: Record<string, any>
  stats?: Record<string, any>
  active_location?: Record<string, any> | null
  calls?: Array<Record<string, any>>
  tank_watch?: Array<Record<string, any>>
  tickets?: Array<Record<string, any>>
  safety?: Array<Record<string, any>>
  activity?: Array<Record<string, any>>
}

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const filter = ref<'all'|'open'|'en_route'|'done'>('open')
const updatedAt = ref(new Date())

const dashboard = ref<Payload>({
  driver: {
    name: 'Haul Driver',
    company_name: 'Demo Oil Services Inc.',
    region: 'North Dakota',
  },
  stats: {
    open_calls: 0,
    en_route: 0,
    completed_today: 0,
    barrels_moved_today: 0,
    tickets_open: 0,
    safety_items: 0,
  },
  active_location: null,
  calls: [],
  tank_watch: [],
  tickets: [],
  safety: [],
  activity: [],
})

const busyId = ref('')
const calls = computed(() => dashboard.value.calls || [])
const filteredCalls = computed(() => {
  if (filter.value === 'all') return calls.value
  return calls.value.filter((call) => {
    const status = (call.status || '').toLowerCase()
    if (filter.value === 'en_route') return status === 'en_route' || status === 'accepted'
    return status === filter.value
  })
})

function typeLabel(type: string) {
  return ({
    bringFresh: 'Bring fresh',
    bringProduction: 'Bring production',
    bringHeavy: 'Bring heavy',
    disposal: 'Take to disposal',
  } as Record<string, string>)[type] || type || 'Haul Call'
}

function isPush(type: string) {
  return type !== 'disposal'
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
}

async function setStatus(call: any, status: string) {
  busyId.value = call.id
  error.value = ''
  try {
    const res = await fetch(`/api/employee/driver-calls/${encodeURIComponent(call.id)}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ status }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    call.status = status
    await loadDashboard(false)
  } catch (e: any) {
    error.value = e.message || 'Could not update call'
  } finally {
    busyId.value = ''
  }
}

function logFromCall(call: any) {
  router.push({ path: '/driver/tickets', query: { fromCall: String(call.id), type: call.type } })
}
const tankWatch = computed(() => dashboard.value.tank_watch || [])
const tickets = computed(() => dashboard.value.tickets || [])
const safety = computed(() => dashboard.value.safety || [])
const activity = computed(() => dashboard.value.activity || [])
const stats = computed(() => dashboard.value.stats || {})

function go(path: string) { router.push(path) }

async function loadDashboard(showSpinner = true) {
  if (showSpinner) loading.value = true
  refreshing.value = !showSpinner
  error.value = ''
  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/employee/driver-calls', {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    if (!response.ok) throw new Error('Unable to load driver dashboard')
    const payload = await response.json()
    dashboard.value = {
      ...dashboard.value,
      ...payload,
      driver: { ...dashboard.value.driver, ...(payload.driver || payload.user || {}) },
      stats: { ...dashboard.value.stats, ...(payload.stats || {}) },
      calls: (payload.calls || payload.driver_calls || dashboard.value.calls).map((call: any) => ({
        ...call,
        status: call.status === 'accepted' ? 'en_route' : call.status,
        action: call.action || (call.type === 'disposal' ? 'pull' : 'push'),
      })),
    }
  } catch (e: any) {
    error.value = e?.message || 'Driver dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    updatedAt.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <DriverAppLayout page-title="Driver Haul Dashboard" page-subtitle="Calls, tanks, and tickets">
    <div class="mx-auto w-full max-w-[1650px]">

      <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/50 p-4 shadow-xl backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
        <div class="flex items-center gap-3">
          <div class="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 font-black text-slate-950">DR</div>
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="text-2xl font-extrabold text-white">Driver Haul Dashboard</h1>
              <span class="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-bold uppercase text-cyan-300">Haul Driver</span>
            </div>
            <p class="mt-1 text-sm text-slate-400">{{ dashboard.driver?.name }} · {{ dashboard.driver?.company_name }} · {{ dashboard.driver?.region }}</p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <button class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 text-sm font-semibold" @click="loadDashboard(false)">{{ refreshing ? 'Refreshing…' : '↻ Refresh' }}</button>
          <button class="rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 px-4 py-2 text-sm font-extrabold text-slate-950" @click="go('/driver/tickets')">Open Tickets</button>
        </div>
      </section>

      <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{{ error }}</div>

      <template v-if="!loading">
        <section class="mb-5 grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
          <DriverMetric title="Open Calls" :value="stats.open_calls ?? 0" subtitle="Waiting for action" tone="amber" icon="call" />
          <DriverMetric title="En Route" :value="stats.en_route ?? 0" subtitle="Active haul" tone="cyan" icon="route" />
          <DriverMetric title="Completed Today" :value="stats.completed_today ?? 0" subtitle="Closed calls" tone="emerald" icon="done" />
          <DriverMetric title="Barrels Moved" :value="stats.barrels_moved_today ?? 0" subtitle="Today" tone="blue" icon="barrel" />
          <DriverMetric title="Open Tickets" :value="stats.tickets_open ?? 0" subtitle="Need closeout" tone="purple" icon="ticket" />
          <DriverMetric title="Safety Items" :value="stats.safety_items ?? 0" subtitle="Need attention" tone="red" icon="safety" />
        </section>

        <section class="mb-5 grid gap-5 xl:grid-cols-12">
          <DriverCard class="xl:col-span-5" title="Current Location" subtitle="Where you're hauling from / to">
            <div v-if="dashboard.active_location" class="rounded-2xl border border-cyan-500/20 bg-cyan-500/[.05] p-5">
              <span class="text-[10px] font-bold uppercase tracking-[.14em] text-cyan-300">Assigned Location</span>
              <h3 class="mt-1 text-xl font-extrabold text-white">{{ dashboard.active_location.well_name || dashboard.active_location.name }}</h3>
              <p class="mt-1 text-sm text-slate-400">{{ dashboard.active_location.location || '' }}</p>
              <div class="mt-4 grid grid-cols-3 gap-3">
                <div class="rounded-xl bg-slate-950/35 p-3"><span class="block text-[10px] text-slate-500">Company Man</span><strong class="mt-1 block text-sm text-white">{{ dashboard.active_location.company_man || '—' }}</strong></div>
                <div class="rounded-xl bg-slate-950/35 p-3"><span class="block text-[10px] text-slate-500">Supervisor</span><strong class="mt-1 block text-sm text-white">{{ dashboard.active_location.supervisor || '—' }}</strong></div>
                <div class="rounded-xl bg-slate-950/35 p-3"><span class="block text-[10px] text-slate-500">Tank Battery</span><strong class="mt-1 block text-sm text-white">{{ dashboard.active_location.tank_count ?? 0 }}</strong></div>
              </div>
            </div>
            <div v-else class="rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">No active location assigned.</div>
          </DriverCard>

          <DriverCard class="xl:col-span-4" title="Driver Actions" subtitle="Fast haul workflow">
            <div class="grid grid-cols-2 gap-3">
              <DriverAction label="Open Calls" icon="call" tone="amber" @click="filter='open'" />
              <DriverAction label="En Route" icon="route" tone="cyan" @click="filter='en_route'" />
              <DriverAction label="Log Push-In" icon="push" tone="emerald" @click="go('/driver')" />
              <DriverAction label="Log Pull" icon="pull" tone="blue" @click="go('/driver')" />
              <DriverAction label="Driver Tickets" icon="ticket" tone="purple" @click="go('/driver/tickets')" />
              <DriverAction label="Tank Watch" icon="tank" tone="red" @click="go('/driver/tanks')" />
            </div>
          </DriverCard>

          <DriverCard class="xl:col-span-3" title="Safety / Exceptions" subtitle="Field issues before hauling">
            <div class="space-y-2">
              <DriverRow v-for="(item,i) in safety.slice(0,5)" :key="item.id || i" :title="item.title || item.type || 'Safety Item'" :subtitle="item.description || item.location || ''" :meta="item.time || ''" :tone="item.severity === 'high' ? 'red' : 'amber'" />
              <div v-if="!safety.length" class="grid min-h-48 place-items-center rounded-xl bg-slate-900/25 text-center"><div><div class="text-2xl">✓</div><p class="mt-2 font-semibold text-white">No safety exceptions</p></div></div>
            </div>
          </DriverCard>
        </section>

        <section class="mb-5 grid gap-5 xl:grid-cols-12">
          <DriverCard class="xl:col-span-7" title="Haul Calls" subtitle="Open, en route and completed calls">
            <div class="mb-4 flex flex-wrap gap-2">
              <button v-for="item in ['all','open','en_route','done']" :key="item" class="rounded-full border px-3 py-1.5 text-xs font-bold capitalize" :class="filter===item ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300':'border-slate-700/60 bg-slate-800/60 text-slate-400'" @click="filter=item as any">
                {{ item.replace('_',' ') }}
              </button>
            </div>
            <div class="space-y-3">
              <div v-for="(call,i) in filteredCalls" :key="call.id || i" class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <span class="text-[10px] font-bold uppercase tracking-wide text-cyan-300">{{ typeLabel(call.type) }}</span>
                    <h3 class="mt-1 font-extrabold text-white">{{ call.tank_name || call.tankName || `Tank ${i+1}` }}</h3>
                    <p class="mt-1 text-xs text-slate-500">{{ call.well_name || call.location || '' }}</p>
                  </div>
                  <div class="text-right"><strong class="text-lg text-white">{{ call.barrels ?? 0 }} BBL</strong><span class="mt-1 block text-[10px] uppercase text-slate-500">{{ call.status === 'en_route' ? 'En route' : (call.status || 'Open') }}</span></div>
                </div>
                <div class="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <button class="rounded-lg border border-slate-700/50 bg-slate-700/30 px-3 py-2 text-xs font-semibold" type="button" @click="go('/driver/tanks')">Directions</button>
                  <button v-if="call.status==='open'" class="rounded-lg bg-cyan-500 px-3 py-2 text-xs font-bold text-slate-950" type="button" :disabled="busyId === call.id" @click="setStatus(call, 'accepted')">En Route</button>
                  <button v-if="call.status !== 'done'" class="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-bold text-slate-950" type="button" @click="logFromCall(call)">{{ isPush(call.type) ? 'Log Push-In' : 'Log Pull' }}</button>
                </div>
              </div>
              <div v-if="!filteredCalls.length" class="rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">No haul calls in this filter.</div>
            </div>
          </DriverCard>

          <DriverCard class="xl:col-span-5" title="Tank Watch" subtitle="Pickup / disposal context" action-label="Open Tank Watch" @action="go('/driver/tanks')">
            <div class="grid gap-3 sm:grid-cols-2">
              <div v-for="(tank,i) in tankWatch.slice(0,6)" :key="tank.id || i" class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                <div class="flex justify-between gap-2"><strong class="text-sm text-white">{{ tank.name || `Tank ${i+1}` }}</strong><span class="text-xs font-bold" :class="Number(tank.fill_pct || 0)>=85 ? 'text-red-300':'text-cyan-300'">{{ tank.fill_pct ?? 0 }}%</span></div>
                <p class="mt-1 truncate text-[10px] text-slate-500">{{ tank.well_name || tank.location || '' }}</p>
                <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-700/50"><div class="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" :style="{width:`${Math.min(100, Number(tank.fill_pct || 0))}%`}"></div></div>
                <div class="mt-2 flex justify-between text-[10px] text-slate-500"><span>{{ tank.contents || 'Production' }}</span><span>{{ tank.total_bbl ?? 0 }} BBL</span></div>
              </div>
              <div v-if="!tankWatch.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-6 text-center text-sm text-slate-500">Tank watch data will appear here.</div>
            </div>
          </DriverCard>
        </section>

        <section class="mb-5 grid gap-5 xl:grid-cols-12">
          <DriverCard class="xl:col-span-4" title="Driver Tickets" subtitle="Loads that still need paperwork" action-label="Open Tickets" @action="go('/driver/tickets')">
            <div class="space-y-2">
              <DriverRow v-for="(item,i) in tickets.slice(0,6)" :key="item.id || i" :title="item.ticket_number || `Ticket ${i+1}`" :subtitle="[item.well_name,item.destination].filter(Boolean).join(' · ')" :meta="item.status || ''" :tone="item.status==='closed'?'emerald':item.status==='rejected'?'red':'purple'" />
              <div v-if="!tickets.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No open driver tickets.</div>
            </div>
          </DriverCard>

          <DriverCard class="xl:col-span-8" title="Recent Haul Activity" subtitle="Calls, loads, tickets and tank events">
            <div class="grid gap-2 md:grid-cols-2">
              <DriverRow v-for="(item,i) in activity.slice(0,8)" :key="item.id || i" :title="item.title || item.description || 'Activity'" :subtitle="item.description && item.title ? item.description : item.location || ''" :meta="item.time || item.created_at || ''" :tone="item.status==='danger'?'red':item.status==='warning'?'amber':item.status==='success'?'emerald':'cyan'" />
              <div v-if="!activity.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">Recent haul activity will appear here.</div>
            </div>
          </DriverCard>
        </section>
      </template>
    </div>
  </DriverAppLayout>
</template>
